// src/data/teachingContent.js
// Complete teaching content for all 20 factors (160 slides total)

export const TEACHING_CONTENT = {
  // ==========================================
  // FACTOR 1: Emotional Stability & Conflict Resolution
  // ==========================================
  1: {
    factorName: "Emotional Stability & Conflict Resolution",
    slides: [
      {
        number: 1,
        type: "title",
        title: "PEACE THAT PREVAILS IN STORMY MOMENTS",
        scripture: "A gentle answer turns away wrath, but a harsh word stirs up anger. — Proverbs 15:1",
        highlight: "When emotions rise, grace must speak louder than ego."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS EMOTIONAL STABILITY?",
        points: [
          "Emotional stability = responding with self-control and wisdom, not reaction",
          "Conflict resolution = addressing issues to heal, not to win",
          "Key traits: calm under pressure | forgiving heart | teachable spirit | peacemaker mindset",
          "Why it matters: It creates a safe emotional climate where love can breathe and trust can grow"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God models emotional stability: 'The Lord is merciful and slow to anger.' — Psalm 103:8",
          "Jesus demonstrated calm authority even under pressure (Mark 4:39)",
          "Conflict handled God's way produces righteousness (James 1:19–20)"
        ],
        insight: "Biblical peace is not the absence of conflict but the presence of Christ in it."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Reacting out of hurt instead of healing the hurt",
          "Silent treatment or withdrawal as emotional weapons",
          "Pride that blocks apology and restoration",
          "Unrealistic expectations that turn into resentment"
        ],
        warning: "When communication stops, conflict starts breathing."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pause and Pray before you speak (Proverbs 15:28)",
          "2. Use 'I feel' statements instead of accusations",
          "3. Seek first to understand, then to be understood (James 1:19)",
          "4. Invite God into every conversation through short prayers for wisdom"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Set rules for fair fighting: no yelling, no interrupting, no name-calling",
          "2. Schedule resolution time — never go to bed angry (Ephesians 4:26)",
          "3. Forgive quickly, even if you're still healing",
          "4. Affirm after resolution: end hard talks with words of love and security"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Repeated verbal or emotional abuse",
          "Stonewalling or complete withdrawal",
          "Physical violence or fear in communication",
          "Lingering bitterness that outlasts apologies"
        ],
        callout: "📞 Seek pastoral or professional counseling before the cycle deepens."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "This week, choose one conflict and apply a new peace strategy.",
        nextStep: "Pray together daily for wisdom in emotional moments.",
        scripture: "Blessed are the peacemakers, for they shall be called children of God. — Matthew 5:9",
        closing: "Every conflict is a classroom where God teaches love."
      }
    ]
  },

  // ==========================================
  // FACTOR 2: Communication Style & Intimacy
  // ==========================================
  2: {
    factorName: "Communication Style & Intimacy",
    slides: [
      {
        number: 1,
        type: "title",
        title: "SPEAK LIFE, BUILD LOVE",
        scripture: "Death and life are in the power of the tongue. — Proverbs 18:21",
        highlight: "Your words can either be the seeds of connection or the spark of conflict."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Communication: expressing thoughts and emotions clearly, kindly, and truthfully",
          "Intimacy: emotional closeness built through honest and consistent connection",
          "Why it matters: The strength of your words determines the depth of your bond",
          "Couples grow apart when they stop talking"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God spoke creation into being — words carry life (Genesis 1:3)",
          "Jesus modeled empathetic listening (John 4:7–26)",
          "Scripture calls for truth in love (Ephesians 4:15)"
        ],
        insight: "Healthy communication mirrors God's heart — truth, grace, and compassion working together."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Talking to be right instead of to be understood",
          "Using silence as punishment or sarcasm as defense",
          "Assuming your partner 'should just know'",
          "Turning disagreements into scoreboards instead of solutions"
        ],
        warning: "When listening stops, loneliness begins — even in marriage."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Be quick to listen, slow to speak, slow to anger (James 1:19)",
          "2. Use loving tone and timing. Hard truths said harshly still wound",
          "3. Affirm often. Appreciation keeps love alive",
          "4. Be transparent. Secrets and assumptions breed distance"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Learn your partner's communication language (gentle reassurance, quality talk, acts of care)",
          "2. Pray before difficult conversations. It calms the spirit",
          "3. Apologize well: own your part without excuses",
          "4. End discussions with connection, not conclusion"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Frequent miscommunication or chronic defensiveness",
          "One partner dominates every discussion",
          "Emotional shutdowns or passive-aggressive behavior",
          "Loss of interest in sharing daily life"
        ],
        callout: "📞 Seek pastoral guidance or a counselor if healthy dialogue feels impossible."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Practice one 'speak life' habit every day this week — compliment, affirm, or appreciate your spouse.",
        nextStep: "Schedule 10 minutes of uninterrupted talk time daily.",
        scripture: "Let your conversation be always full of grace, seasoned with salt. — Colossians 4:6",
        closing: "Love thrives where words flow freely and hearts stay open."
      }
    ]
  },

  // ==========================================
  // FACTOR 3: Financial Management & Values
  // ==========================================
  3: {
    factorName: "Financial Management & Values",
    slides: [
      {
        number: 1,
        type: "title",
        title: "MONEY IN GOD'S HANDS, PEACE IN OUR HOME",
        scripture: "Commit your works to the Lord, and your plans will be established. — Proverbs 16:3",
        highlight: "When couples surrender their finances to God, their home gains both wisdom and peace."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Financial management = handling resources with wisdom, discipline, and shared purpose",
          "Financial values = agreeing on what matters most in how money is earned, spent, and given",
          "Why it matters: Money decisions reveal heart values",
          "Unity here builds lasting stability"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God calls us to stewardship, not ownership (Psalm 24:1)",
          "Faithful stewardship attracts divine provision (Luke 16:10–11)",
          "Generosity reflects God's heart (2 Corinthians 9:6–8)"
        ],
        insight: "A God-centered financial life turns money from a stress point into a worship point."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Lack of transparency in spending or hidden debts",
          "Competing financial priorities and impulsive decisions",
          "Cultural or family pressure to spend beyond means",
          "Neglecting generosity or tithing"
        ],
        warning: "Finances test trust more than income does — secrecy weakens unity."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray before planning — invite God into every financial goal (James 1:5)",
          "2. Create a joint budget that reflects shared priorities",
          "3. Agree on giving — generosity heals greed",
          "4. Discuss every major purchase to prevent resentment later"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Avoid comparison — God's provision for others isn't your standard",
          "2. Save consistently — wisdom plans for tomorrow without fear",
          "3. Keep records together — accountability builds peace",
          "4. Honor God with your tithe — it opens heaven's flow (Malachi 3:10)"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Frequent arguments about money",
          "One spouse controls or hides finances",
          "Chronic debt or irresponsible spending habits",
          "Fear, guilt, or anxiety around giving"
        ],
        callout: "📞 Seek financial counseling or pastoral guidance before small cracks become deep divides."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Review your budget this week — pray together and ask: 'Does this reflect our shared faith and goals?'",
        nextStep: "Set one financial goal you'll achieve as a team (saving, debt-free target, giving).",
        scripture: "Where your treasure is, there your heart will be also. — Matthew 6:21",
        closing: "A couple united in purpose over finances becomes unstoppable in purpose for God."
      }
    ]
  },

  // ==========================================
  // FACTOR 4: Social Compatibility & Family Dynamics
  // ==========================================
  4: {
    factorName: "Social Compatibility & Family Dynamics",
    slides: [
      {
        number: 1,
        type: "title",
        title: "HARMONY BEYOND TWO",
        scripture: "If it is possible, as much as depends on you, live peaceably with all men. — Romans 12:18",
        highlight: "A peaceful marriage is not isolated from people — it is strengthened by godly relationships."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Social compatibility = the ability to relate well to others while keeping unity at home",
          "Family dynamics = how both partners' families, friends, and backgrounds influence marriage",
          "Why it matters: Healthy boundaries preserve peace",
          "Wisdom in relationships builds lasting harmony"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God designed marriage to bless generations, not isolate them (Genesis 12:2–3)",
          "Unity in diversity reflects the body of Christ (1 Corinthians 12:12–14)",
          "Peace in family life glorifies God (Colossians 3:13–15)"
        ],
        insight: "A godly marriage manages relationships with grace, humility, and love."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Over-involvement or interference from family members",
          "Comparison with other couples or relatives",
          "Conflicts over loyalty — 'your side vs. my side'",
          "Lack of agreement about social boundaries or traditions"
        ],
        warning: "Uncontrolled external influence becomes internal chaos."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Agree on boundaries early — unity begins with clarity",
          "2. Honor both families without letting either dominate",
          "3. Handle conflicts privately — don't expose your spouse to family criticism",
          "4. Pray for both families — love wins where words can't reach"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Respect cultural differences but choose kingdom values first",
          "2. Protect your marriage time — don't let social obligations replace intimacy",
          "3. Build couple friendships with shared values",
          "4. Celebrate both sides equally during family events to prevent bias"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Constant tension with in-laws or relatives",
          "One spouse feels excluded from family matters",
          "Family members fueling division or gossip",
          "Cultural or background differences causing persistent conflict"
        ],
        callout: "📞 Seek pastoral mediation before resentment becomes rebellion."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Sit down together and create a 'Family Peace Plan' — list boundaries, traditions, and family commitments you both agree on.",
        nextStep: "Pray for every family connected to your union — healing flows through forgiveness.",
        scripture: "Blessed are the peacemakers, for they shall be called sons of God. — Matthew 5:9",
        closing: "Marriage thrives when peace becomes your family culture."
      }
    ]
  },

  // ==========================================
  // FACTOR 5: Life Planning & Flexibility
  // ==========================================
  5: {
    factorName: "Life Planning & Flexibility",
    slides: [
      {
        number: 1,
        type: "title",
        title: "PLANNING WITH FAITH, ADJUSTING WITH GRACE",
        scripture: "A man's heart plans his way, but the Lord directs his steps. — Proverbs 16:9",
        highlight: "Couples who plan with God never panic when life changes direction."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Life planning = setting shared goals and priorities under God's guidance",
          "Flexibility = trusting God's timing and adjusting plans without losing peace",
          "Why it matters: Rigid plans break under pressure",
          "Spirit-led flexibility builds endurance and unity"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God invites us to plan, but He reserves the right to redirect (James 4:13–15)",
          "Joseph's dreams came true through detours — not shortcuts (Genesis 45:5–8)",
          "The wise build with vision, but they yield to divine correction (Proverbs 19:21)"
        ],
        insight: "In marriage, success is not getting your plan — it's following God's purpose together."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Conflicting goals (career vs. family priorities)",
          "Anxiety or frustration when plans delay or fail",
          "One partner being overly rigid or controlling",
          "Difficulty trusting God during transitions"
        ],
        warning: "When couples stop adapting, they start drifting apart."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray before you plan — seek divine clarity first",
          "2. Set short-term and long-term goals together",
          "3. Write down your shared vision — Habakkuk 2:2",
          "4. Review and adjust your goals annually — God reveals in seasons"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Celebrate progress, not perfection",
          "2. Stay open to God's redirection — don't fight divine detours",
          "3. Encourage each other's dreams without competition",
          "4. Balance ambition with rest and contentment"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Constant disagreement about life goals",
          "Financial or career decisions made in isolation",
          "Refusal to compromise or adapt when life shifts",
          "Feeling spiritually misaligned in direction"
        ],
        callout: "📞 Seek mentoring or pastoral guidance when purpose feels divided."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write one 'Faith-Goal' together — something only God can make possible this year.",
        nextStep: "Create a habit of monthly prayer check-ins for vision alignment.",
        scripture: "Unless the Lord builds the house, they labor in vain who build it. — Psalm 127:1",
        closing: "When plans are flexible and faith is firm, your marriage becomes unstoppable."
      }
    ]
  }

  ,

  // ==========================================
  // FACTOR 6: Love Languages
  // ==========================================
  6: {
    factorName: "Love Languages",
    slides: [
      {
        number: 1,
        type: "title",
        title: "SPEAKING THE LANGUAGE OF LOVE THAT LASTS",
        scripture: "Let all that you do be done with love. — 1 Corinthians 16:14",
        highlight: "Love is not just what you feel — it's what you communicate in a language your partner understands."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT ARE LOVE LANGUAGES?",
        points: [
          "Love languages are the different ways people give and receive love",
          "The five primary love languages: Words of Affirmation, Quality Time, Acts of Service, Receiving Gifts, and Physical Touch",
          "Why it matters: Misunderstood love languages cause emotional distance",
          "Speaking the right one creates deep connection"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God expresses His love in many ways — through Word, presence, giving, service, and touch (John 3:16, Matthew 8:3)",
          "Christ's love is both spoken and demonstrated (Romans 5:8)",
          "We reflect God best when we love others in the way they can feel and recognize"
        ],
        insight: "Biblical love is intentional — it chooses to communicate, not just to feel."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Assuming your spouse feels love the same way you do",
          "Neglecting your partner's emotional needs due to busyness or pride",
          "Giving gifts when they crave time, or words when they need actions",
          "Mistaking physical affection for emotional connection"
        ],
        warning: "Love unspoken in the right language often feels unloved."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Discover each other's love language. Ask, observe, and listen",
          "2. Give love intentionally, not accidentally",
          "3. Practice the 'love deposit' rule — do small acts that fill their emotional bank daily",
          "4. Express gratitude often — appreciation fuels affection"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Rotate expressions of love. Variety keeps connection fresh",
          "2. Avoid using your spouse's weakness as a weapon",
          "3. Communicate your needs clearly and kindly",
          "4. Ask the Holy Spirit to teach you how to love your spouse uniquely"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Feeling constantly unloved despite good intentions",
          "Repeated emotional disconnection or resentment",
          "Intimacy replaced by routine or silence",
          "Struggling to express affection without tension"
        ],
        callout: "📞 Seek marriage mentoring or pastoral guidance when emotional distance deepens."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Intentionally show love this week in your spouse's primary language every day.",
        nextStep: "Discuss what makes each of you feel most loved and write it down.",
        scripture: "Above all, put on love, which binds everything together in perfect harmony. — Colossians 3:14",
        closing: "When you speak love fluently, your home becomes heaven's language made visible."
      }
    ]
  },

  // ==========================================
  // FACTOR 7: Personality & Temperament
  // ==========================================
  7: {
    factorName: "Personality & Temperament",
    slides: [
      {
        number: 1,
        type: "title",
        title: "DIFFERENT BY DESIGN, UNITED BY GRACE",
        scripture: "Having then gifts differing according to the grace that is given to us, let us use them. — Romans 12:6",
        highlight: "Your differences are not a problem to solve — they're a purpose to serve."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Personality and temperament define how each partner thinks, feels, and responds",
          "God created variety — Sanguine, Choleric, Melancholic, and Phlegmatic — to enrich, not divide",
          "Why it matters: Understanding each other's wiring replaces frustration with compassion",
          "Creates teamwork instead of tension"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God gave unique temperaments to fulfill unique assignments (Exodus 31:3, Jeremiah 1:5)",
          "The disciples had different personalities but one mission (Peter bold, John tender, Thomas analytical)",
          "True unity celebrates diversity under divine order (1 Corinthians 12:4–6)"
        ],
        insight: "Personality differences are God's creative strategy, not marital competition."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Misjudging your spouse's temperament as stubbornness or weakness",
          "Expecting your partner to feel, think, or react like you",
          "Using personality as an excuse for poor behavior ('That's just who I am')",
          "Friction between strong-willed and sensitive temperaments"
        ],
        warning: "Clashes begin where empathy ends."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Study your spouse's temperament. Don't react — learn their rhythm",
          "2. Celebrate strengths, cover weaknesses. Marriage is mutual compensation",
          "3. Avoid labeling. Use encouragement, not personality boxes",
          "4. Respond with gentleness. Every temperament blossoms under kindness"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Adjust your approach, not your partner. Speak their emotional language",
          "2. Use 'we' instead of 'me.' Build identity as a team",
          "3. Pray for understanding before correction",
          "4. Leverage differences for ministry and purpose synergy"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Constant personality clashes leading to emotional withdrawal",
          "Pride, criticism, or control disguised as 'leadership'",
          "Resentment toward your spouse's natural tendencies",
          "Emotional exhaustion or loss of admiration"
        ],
        callout: "📞 Seek pastoral or temperament counseling when differences turn into division."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "List three of your spouse's strengths and thank God for them daily this week.",
        nextStep: "Take a personality assessment together and discuss how your differences complement each other.",
        scripture: "Be kindly affectionate to one another with brotherly love, in honor giving preference to one another. — Romans 12:10",
        closing: "God didn't make you identical — He made you ideal for each other's destiny."
      }
    ]
  },

  // ==========================================
  // FACTOR 8: Spiritual Compatibility & Values
  // ==========================================
  8: {
    factorName: "Spiritual Compatibility & Values",
    slides: [
      {
        number: 1,
        type: "title",
        title: "ROOTED IN FAITH, GROWING IN GRACE",
        scripture: "Do not be unequally yoked together with unbelievers. — 2 Corinthians 6:14",
        highlight: "Spiritual harmony is the heartbeat of a lasting, joyful, and purpose-driven marriage."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Spiritual compatibility = walking in unity of faith, purpose, and devotion to God",
          "Shared values = aligning your beliefs, morals, and life goals around biblical truth",
          "Why it matters: Without spiritual unity, emotional and moral foundations crumble",
          "Faith is the anchor of lasting marriage"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "Marriage reflects Christ and the Church (Ephesians 5:25–32)",
          "True unity is built when both partners walk in the Spirit (Galatians 5:16–25)",
          "Prayer together releases divine strength (Matthew 18:19–20)"
        ],
        insight: "Spiritual compatibility isn't about religion — it's about revelation and shared pursuit of God."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "One partner spiritually stronger or more devoted than the other",
          "Disagreements about prayer, church involvement, or giving",
          "Conflicts between faith and cultural/family traditions",
          "Inconsistent spiritual habits that cause drifting"
        ],
        warning: "When spiritual unity breaks, emotional intimacy weakens."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray together daily — even short prayers build strong connection",
          "2. Study God's Word together — share reflections and life applications",
          "3. Worship together — unity grows when couples honor God side by side",
          "4. Serve together in ministry or giving"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Discuss spiritual goals — align on purpose, calling, and family vision",
          "2. Be patient with spiritual growth — maturity develops over time",
          "3. Encourage accountability partners — mentors strengthen marriage",
          "4. Keep Christ at the center of every decision and discussion"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "One partner resists prayer or spiritual conversation",
          "Ongoing tension about faith practices or beliefs",
          "Secret or separate spiritual lives (no shared devotion)",
          "Confusion about purpose or direction"
        ],
        callout: "📞 Seek pastoral guidance when spiritual disconnection causes emotional distance."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Choose one new spiritual practice to do together (devotional, fasting day, or prayer walk).",
        nextStep: "Attend church or Bible study as a couple this week.",
        scripture: "As for me and my house, we will serve the Lord. — Joshua 24:15",
        closing: "A couple that prays together stays aligned with God's plan and each other's heart."
      }
    ]
  },

  // ==========================================
  // FACTOR 9: Parenting Philosophy & Family Vision
  // ==========================================
  9: {
    factorName: "Parenting Philosophy & Family Vision",
    slides: [
      {
        number: 1,
        type: "title",
        title: "RAISING ARROWS, NOT JUST CHILDREN",
        scripture: "Train up a child in the way he should go, and when he is old, he will not depart from it. — Proverbs 22:6",
        highlight: "Parenting isn't about control — it's about cultivation. You're raising tomorrow's leaders."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Parenting philosophy = the beliefs, principles, and style guiding how you raise your children",
          "Family vision = the shared mission and values shaping your home culture",
          "Why it matters: When parents lead with unity and purpose",
          "Children grow with confidence and godly direction"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God entrusts parents with stewardship over souls, not ownership (Psalm 127:3)",
          "Teaching begins at home — faith is generational (Deuteronomy 6:6–7)",
          "Discipline done in love produces righteousness (Hebrews 12:11)"
        ],
        insight: "Parenting is ministry — your home is your first pulpit."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Conflicting discipline methods or inconsistent parenting",
          "Overworking parents leaving emotional gaps",
          "Allowing culture or screens to raise the children",
          "Neglecting prayer and spiritual training at home"
        ],
        warning: "When parents disagree, children become confused; when parents unite, children become secure."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Establish family values early. Write what your home stands for",
          "2. Agree on discipline style — consistent, loving, and fair",
          "3. Create family devotion moments. Teach Scripture and pray together",
          "4. Lead by example. Children mirror what they see, not what they're told"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Balance love with structure",
          "2. Encourage individuality — nurture gifts, not just behavior",
          "3. Protect your home atmosphere — peace, joy, and faith first",
          "4. Plan family time intentionally — togetherness builds trust"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Children showing fear, rebellion, or emotional withdrawal",
          "Parents frequently undermining each other in front of kids",
          "Lack of prayer, affection, or communication at home",
          "Over-reliance on devices or school to teach values"
        ],
        callout: "📞 Seek spiritual mentorship or family counseling before patterns become painful."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write your 'Family Vision Statement' — one sentence describing what your family stands for.",
        nextStep: "Schedule one family devotion night this week.",
        scripture: "As for me and my house, we will serve the Lord. — Joshua 24:15",
        closing: "Strong marriages raise strong families — and strong families raise godly generations."
      }
    ]
  },

  // ==========================================
  // FACTOR 10: Sexual Intimacy & Expectations
  // ==========================================
  10: {
    factorName: "Sexual Intimacy & Expectations",
    slides: [
      {
        number: 1,
        type: "title",
        title: "GOD'S GIFT OF ONENESS",
        scripture: "And the two shall become one flesh; so then they are no longer two, but one flesh. — Mark 10:8",
        highlight: "Sexual intimacy is not just physical — it's spiritual harmony expressed through love, trust, and covenant."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Sexual intimacy = emotional, physical, and spiritual closeness that bonds husband and wife",
          "Expectations = mutual understanding of desires, comfort, and respect",
          "Why it matters: Healthy intimacy nurtures emotional security",
          "Prevents temptation and reflects God's covenant love"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God created sex within marriage as pure and honorable (Hebrews 13:4)",
          "The Song of Solomon celebrates love's beauty and passion without shame",
          "Intimacy mirrors Christ's love for the Church — selfless, faithful, and fulfilling (Ephesians 5:25–28)"
        ],
        insight: "Sex is not defiled in marriage; it's designed by God to deepen unity and joy."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Shame or guilt from past experiences",
          "Unequal sexual drive or unmet expectations",
          "Using sex as a weapon, reward, or manipulation",
          "Ignoring emotional intimacy that precedes physical closeness"
        ],
        warning: "Unspoken expectations often create unseen resentment."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Talk openly about comfort levels and expectations",
          "2. Build emotional intimacy first — affection precedes attraction",
          "3. Be selfless, not selfish — seek your partner's joy, not just your release",
          "4. Keep Christ at the center — prayer strengthens purity and pleasure"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Heal from past wounds through confession, forgiveness, and prayer",
          "2. Create sacred time for connection — intimacy needs intention",
          "3. Protect your bed from offense — resolve conflicts before intimacy",
          "4. Keep learning each other's love rhythm — bodies and seasons change"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Persistent disinterest or avoidance without communication",
          "Infidelity or pornography use",
          "Intimacy linked to fear, shame, or pain",
          "Emotional or physical abuse"
        ],
        callout: "📞 Seek confidential pastoral or professional counseling when intimacy becomes wounded."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Pray together for renewed trust, tenderness, and passion in your union.",
        nextStep: "Schedule regular 'heart connection' conversations outside the bedroom.",
        scripture: "Let the husband render to his wife the affection due her, and likewise also the wife to her husband. — 1 Corinthians 7:3",
        closing: "When intimacy flows from love and understanding, marriage becomes a holy place of joy and peace."
      }
    ]
  }

  ,

  // ==========================================
  // FACTOR 11: Extended Family & Boundaries
  // ==========================================
  11: {
    factorName: "Extended Family & Boundaries",
    slides: [
      {
        number: 1,
        type: "title",
        title: "LOVING FAMILIES WITHOUT LOSING BOUNDARIES",
        scripture: "For this reason a man shall leave his father and mother and be joined to his wife, and the two shall become one flesh. — Genesis 2:24",
        highlight: "Healthy marriages require love that includes family — but loyalty that protects your covenant."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Boundaries = loving limits that protect your marriage from outside interference",
          "Extended family = parents, siblings, and relatives who continue to influence after marriage",
          "Why it matters: A couple that doesn't set boundaries risks divided loyalty",
          "Conflict and emotional exhaustion follow"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "'Leaving and cleaving' is both physical and emotional separation (Genesis 2:24)",
          "Jesus prioritized spiritual family over cultural expectations (Mark 3:31–35)",
          "Honor parents without allowing them to dominate (Ephesians 6:2)"
        ],
        insight: "Boundaries don't dishonor — they define responsibility."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Over-involvement of in-laws in decision-making",
          "Financial dependence or manipulation from relatives",
          "Comparing families or taking sides",
          "Unclear boundaries leading to resentment or tension"
        ],
        warning: "When families compete for control, the couple loses peace."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Agree on boundaries together — unity first, communication second",
          "2. Present a united front when dealing with family issues",
          "3. Honor without enabling — respect parents, but protect privacy",
          "4. Be transparent, not defensive — explain decisions with love, not anger"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Establish family traditions of your own",
          "2. Keep sensitive issues within your marriage",
          "3. Support each other's parents respectfully and fairly",
          "4. Pray for your in-laws regularly — love breaks tension"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Frequent interference in decisions (finances, parenting, or intimacy)",
          "One partner taking the side of their parents against the other",
          "Emotional manipulation disguised as 'family loyalty'",
          "Constant guilt or fear when saying 'no'"
        ],
        callout: "📞 Seek pastoral counseling when boundaries cause repeated conflict or disrespect."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write down three healthy boundaries that will protect your peace as a couple.",
        nextStep: "Communicate those boundaries together in love to your families.",
        scripture: "If possible, as far as it depends on you, live at peace with everyone. — Romans 12:18",
        closing: "A wise couple knows how to keep the door of love open, but the walls of unity strong."
      }
    ]
  },

  // ==========================================
  // FACTOR 12: Career & Life Goals
  // ==========================================
  12: {
    factorName: "Career & Life Goals",
    slides: [
      {
        number: 1,
        type: "title",
        title: "PURSUING PURPOSE TOGETHER",
        scripture: "Commit to the Lord whatever you do, and He will establish your plans. — Proverbs 16:3",
        highlight: "A successful marriage is not two careers competing — but two callings complementing."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Career & life goals = the dreams, ambitions, and long-term visions each partner holds",
          "Alignment = ensuring those goals harmonize with God's purpose and your marital unity",
          "Why it matters: Without shared vision, couples chase success but lose connection",
          "With alignment, every achievement becomes partnership in purpose"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God called Adam and Eve together to work, multiply, and steward creation (Genesis 1:28)",
          "Ruth and Boaz found destiny through partnership, not independence (Ruth 2–4)",
          "True success is walking in the purpose God assigned (Joshua 1:8)"
        ],
        insight: "When your work honors God and supports your union, your career becomes a calling."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Career competition instead of collaboration",
          "Imbalance between work and home life",
          "Lack of clarity on priorities — family vs. ambition",
          "One partner feeling unsupported or unseen in their goals"
        ],
        warning: "When careers compete, communication suffers — and intimacy fades."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Discuss individual goals early. Don't assume — align",
          "2. Pray over your plans together. Let God be the CEO of your home",
          "3. Celebrate each other's achievements without comparison",
          "4. Balance time and energy — success should not cost your spouse's peace"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Budget for your dreams — financial planning sustains vision",
          "2. Be flexible with seasons. Sometimes one career leads, sometimes both grow",
          "3. Involve your spouse in key decisions",
          "4. Seek purpose-driven success — not just profit or prestige"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Growing resentment over who sacrifices more",
          "Workaholism or neglect of family",
          "Secret financial or career moves",
          "No shared discussion about future direction"
        ],
        callout: "📞 Seek pastoral or professional counsel when ambition starts eroding affection."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Pray and write down one shared long-term life goal (career, ministry, or legacy).",
        nextStep: "Create a 1-year and 5-year 'family vision plan' guided by prayer.",
        scripture: "Two are better than one, because they have a good reward for their labor. — Ecclesiastes 4:9",
        closing: "When your dreams align under God's plan, your marriage becomes unstoppable purpose in motion."
      }
    ]
  },

  // ==========================================
  // FACTOR 13: Emotional Intelligence
  // ==========================================
  13: {
    factorName: "Emotional Intelligence",
    slides: [
      {
        number: 1,
        type: "title",
        title: "MASTERING EMOTIONS, BUILDING CONNECTION",
        scripture: "He who is slow to anger is better than the mighty, and he who rules his spirit than he who takes a city. — Proverbs 16:32",
        highlight: "A wise couple doesn't suppress emotions — they steward them."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Emotional intelligence (EI) = the ability to recognize, understand, and manage your emotions wisely",
          "Key traits: self-awareness, empathy, emotional control, and healthy communication",
          "Why it matters: Emotional immaturity creates distance",
          "Emotional intelligence creates understanding and unity"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "Jesus expressed emotions with purpose, not impulse — compassion (Matthew 9:36), sorrow (John 11:35), righteous anger (Mark 11:15–17)",
          "The fruit of the Spirit includes emotional control — love, peace, patience, gentleness (Galatians 5:22–23)",
          "Mature love governs feelings, not the other way around (1 Corinthians 13:5)"
        ],
        insight: "God calls couples to rule their emotions, not be ruled by them."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Reacting in anger instead of responding in love",
          "Misinterpreting tone, silence, or moods",
          "Emotional shutdowns or overreactions during conflict",
          "Using emotions to manipulate or withdraw"
        ],
        warning: "Uncontrolled emotions can sabotage even Spirit-filled marriages."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pause before responding — silence can be holy",
          "2. Name your emotion — clarity leads to healing",
          "3. Listen to understand, not to defend",
          "4. Pray before reacting — the Holy Spirit calms storms within"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Validate your partner's feelings — empathy heals faster than logic",
          "2. Express emotion with respect, not rage",
          "3. Take time-outs when emotions run high",
          "4. Celebrate emotional honesty, not perfection"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Constant defensiveness, blame, or criticism",
          "Frequent emotional outbursts or cold withdrawal",
          "Unresolved anger from past wounds",
          "Lack of empathy or accountability"
        ],
        callout: "📞 Seek counseling or pastoral help when emotions consistently lead to hurt, not healing."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Identify one emotional habit that needs renewing and pray about it together.",
        nextStep: "Practice one 'slow-to-speak' moment this week in conversation.",
        scripture: "A soft answer turns away wrath, but a harsh word stirs up anger. — Proverbs 15:1",
        closing: "When couples master emotions through the Spirit, they create an atmosphere where love can grow deeply and peacefully."
      }
    ]
  },

  // ==========================================
  // FACTOR 14: Trust & Past Relationships
  // ==========================================
  14: {
    factorName: "Trust & Past Relationships",
    slides: [
      {
        number: 1,
        type: "title",
        title: "BUILDING TRUST, HEALING FROM THE PAST",
        scripture: "Love bears all things, believes all things, hopes all things, endures all things. — 1 Corinthians 13:7",
        highlight: "Trust is not given — it's built through truth, consistency, and grace."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Trust = the confidence that your partner is safe, honest, and faithful with your heart",
          "Healing from the past = releasing old hurts, betrayals, or insecurities that affect the present",
          "Why it matters: Without trust, love becomes fear",
          "Without healing, new relationships carry old wounds"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God restores broken trust through forgiveness and renewal (Psalm 51:10–12)",
          "True love 'keeps no record of wrongs' (1 Corinthians 13:5)",
          "Joseph forgave betrayal and found peace (Genesis 50:20)"
        ],
        insight: "A godly marriage stands on honesty, mercy, and mutual faithfulness — just as God relates with us."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Past betrayals or emotional wounds carried into the new relationship",
          "Insecurity, jealousy, or secret checking of phones/messages",
          "Failure to forgive or discuss past pain openly",
          "One partner rebuilding trust while the other remains defensive"
        ],
        warning: "Old wounds unhealed become new barriers to love."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Be transparent and truthful — secrecy feeds suspicion",
          "2. Discuss past relationships openly — clarity builds safety",
          "3. Establish accountability — honesty in small things strengthens big trust",
          "4. Ask for forgiveness quickly — humility heals faster than hiding"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Avoid comparisons — your spouse is not your past",
          "2. Pray together for emotional cleansing",
          "3. Reassure through actions, not just words",
          "4. Let time and consistency prove reliability"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Emotional withdrawal or suspicion that never fades",
          "Repeated dishonesty or half-truths",
          "Hidden communication or unresolved guilt",
          "Refusal to forgive or rebuild"
        ],
        callout: "📞 Seek pastoral or counseling support when past wounds continually interrupt present peace."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write one 'trust statement' together — a declaration of how you'll protect each other's hearts.",
        nextStep: "Pray over each other's past hurts and declare a clean emotional slate.",
        scripture: "Perfect love casts out fear. — 1 John 4:18",
        closing: "When trust is rebuilt under God's grace, love grows stronger than fear and purer than pain."
      }
    ]
  },

  // ==========================================
  // FACTOR 15: Sexual & Physical Boundaries
  // ==========================================
  15: {
    factorName: "Sexual & Physical Boundaries",
    slides: [
      {
        number: 1,
        type: "title",
        title: "HONOR GOD WITH YOUR BODIES",
        scripture: "Flee from sexual immorality... You were bought at a price. Therefore honor God with your bodies. — 1 Corinthians 6:18–20",
        highlight: "Purity before marriage protects intimacy within marriage."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Sexual & physical boundaries = clear limits that honor God and protect purity before marriage",
          "It includes decisions about physical affection, alone time, and avoiding temptation",
          "Why it matters: Boundaries today build trust for tomorrow",
          "What you sow in purity, you reap in joy"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God calls believers to sexual purity (1 Thessalonians 4:3–4)",
          "Your body is the temple of the Holy Spirit (1 Corinthians 6:19)",
          "Fleeing temptation shows wisdom, not weakness (2 Timothy 2:22)"
        ],
        insight: "True love waits — not out of fear, but out of honor for God and each other."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Cultural pressure or past habits compromising boundaries",
          "One partner pushing limits while the other resists",
          "Lack of accountability or mentorship",
          "Rationalizing compromise ('We're engaged anyway')"
        ],
        warning: "Boundaries ignored before marriage become habits tolerated after."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Define your boundaries together early and clearly",
          "2. Avoid compromising situations (overnight stays, isolation, alcohol)",
          "3. Invite accountability from trusted mentors or pastors",
          "4. Pray together for strength and self-control"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Confess and repent quickly if you fall — grace restores",
          "2. Focus on emotional and spiritual intimacy now",
          "3. Celebrate progress, not perfection",
          "4. Keep your eyes on the wedding day reward"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Repeated boundary violations without remorse",
          "Secrecy or hiding interactions",
          "Pornography use or emotional affairs",
          "Pressure or manipulation around physical intimacy"
        ],
        callout: "📞 Seek immediate pastoral help when purity is consistently compromised."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write down your shared physical boundaries and sign it as a covenant before God.",
        nextStep: "Find an accountability partner or couple to check in with weekly.",
        scripture: "Blessed are the pure in heart, for they will see God. — Matthew 5:8",
        closing: "Purity preserved today becomes intimacy enjoyed for a lifetime."
      }
    ]
  }

  ,

  // ==========================================
  // FACTOR 16: Spiritual Maturity & Shared Faith
  // ==========================================
  16: {
    factorName: "Spiritual Maturity & Shared Faith",
    slides: [
      {
        number: 1,
        type: "title",
        title: "GROWING DEEPER TOGETHER IN CHRIST",
        scripture: "But grow in the grace and knowledge of our Lord and Savior Jesus Christ. — 2 Peter 3:18",
        highlight: "A marriage that grows spiritually together never grows apart."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Spiritual maturity = deepening your relationship with God individually and as a couple",
          "Shared faith = unified pursuit of Christ, worship, and kingdom purpose",
          "Why it matters: Spiritual growth strengthens every other area of marriage",
          "When Christ is central, everything else falls into place"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "Jesus is the vine, we are the branches — apart from Him we can do nothing (John 15:5)",
          "A threefold cord is not quickly broken (Ecclesiastes 4:12)",
          "Spiritual disciplines build endurance and character (Romans 5:3–5)"
        ],
        insight: "The strongest marriages have three partners: husband, wife, and God."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Unequal spiritual growth or maturity levels",
          "Busyness replacing consistent prayer and Bible study",
          "One partner leading spiritually while the other coasts",
          "Spiritual laziness or lack of hunger for God"
        ],
        warning: "When spiritual growth stops, everything else starts to wither."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray together daily — start with 5 minutes, build from there",
          "2. Read Scripture together and discuss what God is teaching you",
          "3. Attend church and serve in ministry as a team",
          "4. Fast together periodically for breakthrough and clarity"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Share testimonies of God's faithfulness with each other",
          "2. Worship together at home — create a sacred atmosphere",
          "3. Study biblical marriage principles together",
          "4. Seek mentorship from spiritually mature couples"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "One partner spiritually stagnant or resistant to growth",
          "No shared spiritual practices or devotional life",
          "Spiritual pride or judgment toward partner",
          "Loss of passion for God's presence and Word"
        ],
        callout: "📞 Seek pastoral mentoring or spiritual direction when growth has plateaued."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Start one new spiritual discipline together this week (daily prayer, Scripture reading, or worship).",
        nextStep: "Schedule a monthly 'spiritual check-in' to discuss your growth and God's direction.",
        scripture: "As for me and my house, we will serve the Lord. — Joshua 24:15",
        closing: "When you grow toward God, you naturally grow toward each other."
      }
    ]
  },

  // ==========================================
  // FACTOR 17: Decision-Making & Leadership Dynamics
  // ==========================================
  17: {
    factorName: "Decision-Making & Leadership Dynamics",
    slides: [
      {
        number: 1,
        type: "title",
        title: "LEADING TOGETHER IN HUMILITY",
        scripture: "Submit to one another out of reverence for Christ. — Ephesians 5:21",
        highlight: "Great marriages aren't built on who's in charge, but on who yields first in love."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Decision-making = how couples discuss, pray, and choose direction together",
          "Leadership dynamics = the balance of responsibility, initiative, and mutual submission",
          "Why it matters: Unity in decision-making prevents power struggles",
          "Healthy leadership creates peace, not pressure"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "Christ-like leadership serves, not dominates (Mark 10:42–45)",
          "Mutual submission reflects love for Christ (Ephesians 5:21)",
          "Wisdom seeks counsel, not control (Proverbs 15:22)"
        ],
        insight: "Biblical leadership in marriage is about responsibility, not rank."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Power struggles or competition for control",
          "One partner making decisions in isolation",
          "Ignoring each other's input or wisdom",
          "Cultural expectations clashing with biblical principles"
        ],
        warning: "When decisions become battles, unity becomes a casualty."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray together before major decisions — seek God's will first",
          "2. Listen fully to each other's perspective without interrupting",
          "3. Identify each other's strengths and delegate accordingly",
          "4. When you disagree, give time and grace before forcing resolution"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Practice 'agree to disagree' on non-essential matters",
          "2. Defer to expertise — trust each other's knowledge areas",
          "3. Make decisions together on major issues (finances, parenting, moves)",
          "4. Celebrate when humility wins over ego"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "One partner dominating or dismissing the other consistently",
          "Frequent arguments ending in impasse or resentment",
          "Secret decisions made without consultation",
          "Lack of spiritual alignment in direction"
        ],
        callout: "📞 Seek mentoring from mature couples when leadership becomes a wedge instead of a bond."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Identify one decision you need to make together this week and commit to praying about it first.",
        nextStep: "Discuss how you'll handle decision-making in your marriage — create a shared agreement.",
        scripture: "Plans fail for lack of counsel, but with many advisers they succeed. — Proverbs 15:22",
        closing: "When humility leads, love follows — and unity thrives."
      }
    ]
  },

  // ==========================================
  // FACTOR 18: Forgiveness, Grace & Healing
  // ==========================================
  18: {
    factorName: "Forgiveness, Grace & Healing",
    slides: [
      {
        number: 1,
        type: "title",
        title: "FORGIVENESS: THE HEARTBEAT OF LASTING LOVE",
        scripture: "Be kind and compassionate to one another, forgiving each other, just as in Christ God forgave you. — Ephesians 4:32",
        highlight: "A marriage without forgiveness is a prison; a marriage filled with grace is paradise."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Forgiveness = choosing to release offense and extend grace",
          "Grace = unmerited favor that covers faults and restores relationship",
          "Healing = the process of emotional and spiritual restoration after hurt",
          "Why it matters: Unforgiveness kills love; grace revives it"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God's forgiveness toward us is the model for our forgiveness of others (Colossians 3:13)",
          "Jesus taught unlimited forgiveness — seventy times seven (Matthew 18:21–22)",
          "Love keeps no record of wrongs (1 Corinthians 13:5)"
        ],
        insight: "The depth of your forgiveness reveals the maturity of your love."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Holding grudges or bringing up past offenses",
          "Demanding apologies instead of extending grace first",
          "Pride that blocks reconciliation",
          "Emotional wounds left unhealed, festering beneath the surface"
        ],
        warning: "Bitterness is a slow poison that destroys from within."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Forgive quickly — don't let the sun go down on your anger (Ephesians 4:26)",
          "2. Apologize sincerely without excuses or blame-shifting",
          "3. Let go of the past — don't weaponize old wounds",
          "4. Pray for your spouse when you're hurt — it softens the heart"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Speak blessing over your spouse, not curses",
          "2. Choose empathy — try to understand why they hurt you",
          "3. Seek healing prayer for deep wounds that won't release",
          "4. Celebrate reconciliation — restore with love and affirmation"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Persistent unforgiveness turning into bitterness",
          "Emotional distance or coldness after conflicts",
          "Repeated offenses without genuine repentance",
          "Deep wounds from betrayal or trauma"
        ],
        callout: "📞 Seek pastoral counseling or inner healing ministry when forgiveness feels impossible."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "If there's any unforgiveness in your heart, release it this week through prayer and conversation.",
        nextStep: "Practice daily gratitude — thank God for three things about your spouse.",
        scripture: "Bear with each other and forgive one another... Forgive as the Lord forgave you. — Colossians 3:13",
        closing: "Grace is the language of heaven — make it the language of your home."
      }
    ]
  },

  // ==========================================
  // FACTOR 19: Purpose, Calling & Kingdom Partnership
  // ==========================================
  19: {
    factorName: "Purpose, Calling & Kingdom Partnership",
    slides: [
      {
        number: 1,
        type: "title",
        title: "MARRIAGE WITH A MISSION",
        scripture: "For we are God's handiwork, created in Christ Jesus to do good works. — Ephesians 2:10",
        highlight: "Your marriage isn't just about you — it's about the kingdom work God assigned you together."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Purpose = God's reason for bringing you together",
          "Calling = the specific assignment or ministry He's entrusted to you as a couple",
          "Kingdom partnership = using your marriage to advance God's kingdom",
          "Why it matters: Purpose gives meaning beyond yourselves"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "God created marriage to reflect His glory and multiply His image (Genesis 1:27–28)",
          "Every believer is called to make disciples (Matthew 28:19–20)",
          "Marriage partnerships in Scripture accomplished great things (Priscilla & Aquila, Acts 18)"
        ],
        insight: "Marriage is not just companionship — it's co-mission with God."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Focusing on personal happiness over kingdom purpose",
          "Lack of clarity on God's calling for the marriage",
          "Busyness crowding out mission and ministry",
          "Fear or complacency blocking obedience"
        ],
        warning: "A marriage without purpose drifts; a marriage with mission thrives."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Pray and seek God's vision for your marriage together",
          "2. Identify your shared gifts, passions, and burdens",
          "3. Look for opportunities to serve together (church, community, missions)",
          "4. Write a mission statement for your family"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Invest in discipleship — mentor younger couples",
          "2. Practice hospitality — open your home for ministry",
          "3. Give generously to kingdom work",
          "4. Raise children with a kingdom mindset"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Marriage feels empty or self-focused",
          "No spiritual fruit or impact beyond yourselves",
          "Disagreement on calling or ministry direction",
          "Neglecting spiritual gifts and opportunities"
        ],
        callout: "📞 Seek spiritual direction or mentorship to discover your shared calling."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Write your marriage mission statement this week — what kingdom work is God calling you to?",
        nextStep: "Identify one way you can serve together this month (volunteer, mentor, host, give).",
        scripture: "Whatever you do, work at it with all your heart, as working for the Lord. — Colossians 3:23",
        closing: "When your marriage has a mission, every day becomes meaningful."
      }
    ]
  },

  // ==========================================
  // FACTOR 20: Technology & Social Media Boundaries
  // ==========================================
  20: {
    factorName: "Technology & Social Media Boundaries",
    slides: [
      {
        number: 1,
        type: "title",
        title: "STAY CONNECTED WITHOUT LOSING CONNECTION",
        scripture: "All things are lawful for me, but not all things are helpful. — 1 Corinthians 10:23",
        highlight: "Technology should serve your marriage, not steal from it."
      },
      {
        number: 2,
        type: "content",
        title: "WHAT IS IT?",
        points: [
          "Technology boundaries = healthy limits on device use, screen time, and online interactions",
          "Social media boundaries = guidelines for what you share, follow, and engage with online",
          "Why it matters: Technology can build closeness or breed distraction and comparison",
          "Intentionality determines which it becomes"
        ]
      },
      {
        number: 3,
        type: "content",
        title: "BIBLICAL FOUNDATION",
        points: [
          "Self-control is a fruit of the Spirit (Galatians 5:22–23)",
          "Guard your heart and eyes from temptation (Proverbs 4:23, Matthew 5:28)",
          "Be wise and discerning in all things (Ephesians 5:15–16)"
        ],
        insight: "Digital holiness is still holiness — what you do online matters to God."
      },
      {
        number: 4,
        type: "content",
        title: "COMMON CHALLENGES",
        points: [
          "Excessive screen time replacing quality time together",
          "Social media comparison stealing contentment",
          "Inappropriate online relationships or content",
          "Phones at meals, in bed, or during conversations"
        ],
        warning: "What starts as distraction can become division."
      },
      {
        number: 5,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 1)",
        points: [
          "1. Create tech-free zones (bedroom, dinner table, date nights)",
          "2. Agree on transparency — no secret accounts or hidden messages",
          "3. Set daily screen time limits together",
          "4. Use technology to strengthen your marriage (devotional apps, shared calendars)"
        ]
      },
      {
        number: 6,
        type: "content",
        title: "PRACTICAL STRATEGIES (PART 2)",
        points: [
          "1. Follow accounts that inspire, not discourage your marriage",
          "2. Never air marital issues online — protect your privacy",
          "3. Practice a weekly 'digital Sabbath' — no screens for 24 hours",
          "4. Celebrate real-life moments instead of just posting them"
        ]
      },
      {
        number: 7,
        type: "content",
        title: "RED FLAGS & WHEN TO SEEK HELP",
        points: [
          "Phone secrecy or defensiveness about device use",
          "Pornography or inappropriate content consumption",
          "Emotional affairs or inappropriate online relationships",
          "Constant comparison to others online"
        ],
        callout: "📞 Seek accountability and counseling when technology becomes a wedge in your marriage."
      },
      {
        number: 8,
        type: "action",
        title: "COMMITMENT & CALL TO ACTION",
        challenge: "Create a 'Technology Covenant' together — write down your agreed boundaries.",
        nextStep: "Implement one tech-free zone or time this week and protect it.",
        scripture: "Whatever you do, do it all for the glory of God. — 1 Corinthians 10:31",
        closing: "When you master technology instead of being mastered by it, your marriage thrives."
      }
    ]
  }
};

export default TEACHING_CONTENT;